import { createRpcClientV2 } from 'rpc-client-utils';
import http from 'hub-http/clients/apiClient';
import adaptiveIdpAuthApiClient from 'hub-http-janus/clients/adaptiveIdpAuthApiClient';
import {
  resolveAvatarsByIdentifierRpc,
  setAvatarOverrideRpc,
  clearAvatarOverrideRpc,
} from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc';
import { resolveAvatarsByIdentifierRpc as resolveAvatarsByIdentifierRpcInternal } from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc-internal';
import type {
  AvatarOverrideResponse,
  AvatarsByIdentifierResponse,
  ResolveAvatarsByIdentifierRequestType,
} from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc';
import invariant from 'react-utils/invariant';
import { arrayBufferToBase64 } from '../lib/arrayBufferToBase64';
import { LOOKUP_TYPE_TO_OBJECT_TYPE_ID } from '../Constants';

export type AuthConfig =
  | { auth: 'app' }
  | { auth: 'internal'; portalId: number };

export function getAuthKey(authConfig?: AuthConfig): string {
  return authConfig?.auth === 'internal'
    ? `internal:${authConfig.portalId}`
    : 'app';
}

export function getAuthConfig(authKey: string | undefined): AuthConfig {
  if (typeof authKey === 'undefined' || authKey === 'app') {
    return { auth: 'app' };
  }

  const [auth, portalIdString, ...extraParts] = authKey.split(':');
  const portalId = Number(portalIdString);

  invariant(
    auth === 'internal' && extraParts.length === 0 && !Number.isNaN(portalId),
    'Unknown auth key "%s"',
    authKey
  );

  return { auth: 'internal', portalId };
}

const appRpcClient = createRpcClientV2({ hubHttpClient: http });
const internalRpcClient = createRpcClientV2({
  hubHttpClient: adaptiveIdpAuthApiClient,
});

const SOURCE_ID = 'CRM_UI';

function resolveObjectTypeId(objectType: string): string {
  const objectTypeId = LOOKUP_TYPE_TO_OBJECT_TYPE_ID[objectType];
  invariant(
    objectTypeId,
    'Unknown objectType "%s" — expected one of: %s',
    objectType,
    Object.keys(LOOKUP_TYPE_TO_OBJECT_TYPE_ID).join(', ')
  );
  return objectTypeId;
}

export function callResolveAvatarsByIdentifier(
  request: ResolveAvatarsByIdentifierRequestType,
  authConfig?: AuthConfig
): Promise<AvatarsByIdentifierResponse> {
  if (authConfig?.auth === 'internal') {
    return internalRpcClient.call(resolveAvatarsByIdentifierRpcInternal, {
      request,
      portalId: authConfig.portalId,
    });
  }
  return appRpcClient.call(resolveAvatarsByIdentifierRpc, { request });
}

export function callSetAvatarOverride(
  objectType: string,
  objectId: number,
  imageData: ArrayBuffer
): Promise<AvatarOverrideResponse> {
  const objectTypeId = resolveObjectTypeId(objectType);
  return appRpcClient.call(setAvatarOverrideRpc, {
    request: {
      sourceId: SOURCE_ID,
      imageData: arrayBufferToBase64(imageData),
      objectTypeAndId: { objectTypeId, objectId },
    },
  });
}

export function callClearAvatarOverride(
  objectType: string,
  objectId: number
): Promise<AvatarOverrideResponse> {
  const objectTypeId = resolveObjectTypeId(objectType);
  return appRpcClient.call(clearAvatarOverrideRpc, {
    request: {
      sourceId: SOURCE_ID,
      objectTypeAndId: { objectTypeId, objectId },
    },
  });
}
